import React, { useEffect } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getLanguageFromPath, ROUTE_SLUGS, translatePath } from '../config/routes'
import SEOHead from './common/SEOHead'

// Lazy loading das páginas
const Home = React.lazy(() => import('../pages/institutional/Home'))
const About = React.lazy(() => import('../pages/institutional/About'))
const Features = React.lazy(() => import('../pages/institutional/Features'))
const Pricing = React.lazy(() => import('../pages/institutional/Pricing'))
const Cases = React.lazy(() => import('../pages/institutional/Cases'))
const Contact = React.lazy(() => import('../pages/institutional/Contact'))
const Platform = React.lazy(() => import('../pages/institutional/Platform'))
const Privacy = React.lazy(() => import('../pages/institutional/Privacy'))
const Terms = React.lazy(() => import('../pages/institutional/Terms'))
const LGPD = React.lazy(() => import('../pages/institutional/LGPD'))
const Security = React.lazy(() => import('../pages/institutional/Security'))
const WorkflowDemo = React.lazy(() => import('../pages/institutional/WorkflowDemo'))
const SmallBusiness = React.lazy(() => import('../pages/institutional/SmallBusiness'))
const Api = React.lazy(() => import('../pages/institutional/Api'))
const HR = React.lazy(() => import('../pages/institutional/HR'))
const IcpBrasil = React.lazy(() => import('../pages/institutional/IcpBrasil'))
const Law = React.lazy(() => import('../pages/institutional/Law'))
const RealEstate = React.lazy(() => import('../pages/institutional/RealEstate'))
const Healthcare = React.lazy(() => import('../pages/institutional/Healthcare'))
const Blog = React.lazy(() => import('../pages/institutional/Blog'))
const BlogPost = React.lazy(() => import('../pages/institutional/BlogPost'))
const SignupLandingPage = React.lazy(() => import('../pages/landing-pages/SignupLandingPage'))
const NotFound = React.lazy(() => import('../pages/NotFound'))

/**
 * LocalizedRoutes - Wrapper component for i18n routing
 * Syncs URL language prefix with i18n and renders correct routes
 */
const LocalizedRoutes = ({ layout: Layout }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { i18n } = useTranslation()

    // Sync language from URL to i18n
    useEffect(() => {
        const urlLanguage = getLanguageFromPath(location.pathname)

        if (i18n.language !== urlLanguage) {
            i18n.changeLanguage(urlLanguage)
            localStorage.setItem('i18nextLng', urlLanguage)
        }
    }, [location.pathname, i18n])

    // Handle legacy query param redirects
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const langParam = params.get('lang')

        if (langParam) {
            let targetLang = null
            if (langParam === 'en') targetLang = 'en-US'
            else if (langParam === 'es') targetLang = 'es-ES'

            if (targetLang) {
                const newPath = translatePath(location.pathname, targetLang)
                navigate(newPath, { replace: true })
            }
        }
    }, [location.search, location.pathname, navigate])

    return (
        <>
            <SEOHead />
            <Routes>
                {/* Portuguese routes (default - no prefix) */}
                <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path={ROUTE_SLUGS.about['pt-BR']} element={<About />} />
                    <Route path={ROUTE_SLUGS.features['pt-BR']} element={<Features />} />
                    <Route path={ROUTE_SLUGS.pricing['pt-BR']} element={<Pricing />} />
                    <Route path={ROUTE_SLUGS.cases['pt-BR']} element={<Cases />} />
                    <Route path={ROUTE_SLUGS.contact['pt-BR']} element={<Contact />} />
                    <Route path={ROUTE_SLUGS.platform['pt-BR']} element={<Platform />} />
                    <Route path={ROUTE_SLUGS.workflow_demo['pt-BR']} element={<WorkflowDemo />} />
                    <Route path={ROUTE_SLUGS.small_business['pt-BR']} element={<SmallBusiness />} />
                    <Route path={ROUTE_SLUGS.api['pt-BR']} element={<Api />} />
                    <Route path={ROUTE_SLUGS.hr['pt-BR']} element={<HR />} />
                    <Route path={ROUTE_SLUGS.icp_brasil['pt-BR']} element={<IcpBrasil />} />
                    <Route path={ROUTE_SLUGS.law['pt-BR']} element={<Law />} />
                    <Route path={ROUTE_SLUGS.real_estate['pt-BR']} element={<RealEstate />} />
                    <Route path={ROUTE_SLUGS.healthcare['pt-BR']} element={<Healthcare />} />
                    <Route path={`${ROUTE_SLUGS.blog['pt-BR']}/:slug`} element={<BlogPost />} />
                    <Route path={ROUTE_SLUGS.blog['pt-BR']} element={<Blog />} />
                    <Route path={ROUTE_SLUGS.privacy['pt-BR']} element={<Privacy />} />
                    <Route path={ROUTE_SLUGS.terms['pt-BR']} element={<Terms />} />
                    <Route path={ROUTE_SLUGS.lgpd['pt-BR']} element={<LGPD />} />
                    <Route path={ROUTE_SLUGS.security['pt-BR']} element={<Security />} />
                    <Route path={ROUTE_SLUGS.landing_pages['pt-BR']} element={<SignupLandingPage />} />
                </Route>

                {/* English routes (/en prefix) */}
                <Route path="/en" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path={ROUTE_SLUGS.about['en-US']} element={<About />} />
                    <Route path={ROUTE_SLUGS.features['en-US']} element={<Features />} />
                    <Route path={ROUTE_SLUGS.pricing['en-US']} element={<Pricing />} />
                    <Route path={ROUTE_SLUGS.cases['en-US']} element={<Cases />} />
                    <Route path={ROUTE_SLUGS.contact['en-US']} element={<Contact />} />
                    <Route path={ROUTE_SLUGS.platform['en-US']} element={<Platform />} />
                    <Route path={ROUTE_SLUGS.workflow_demo['en-US']} element={<WorkflowDemo />} />
                    <Route path={ROUTE_SLUGS.small_business['en-US']} element={<SmallBusiness />} />
                    <Route path={ROUTE_SLUGS.api['en-US']} element={<Api />} />
                    <Route path={ROUTE_SLUGS.hr['en-US']} element={<HR />} />
                    <Route path={ROUTE_SLUGS.icp_brasil['en-US']} element={<IcpBrasil />} />
                    <Route path={ROUTE_SLUGS.law['en-US']} element={<Law />} />
                    <Route path={ROUTE_SLUGS.real_estate['en-US']} element={<RealEstate />} />
                    <Route path={ROUTE_SLUGS.healthcare['en-US']} element={<Healthcare />} />
                    <Route path={`${ROUTE_SLUGS.blog['en-US']}/:slug`} element={<BlogPost />} />
                    <Route path={ROUTE_SLUGS.blog['en-US']} element={<Blog />} />
                    <Route path={ROUTE_SLUGS.privacy['en-US']} element={<Privacy />} />
                    <Route path={ROUTE_SLUGS.terms['en-US']} element={<Terms />} />
                    <Route path={ROUTE_SLUGS.lgpd['en-US']} element={<LGPD />} />
                    <Route path={ROUTE_SLUGS.security['en-US']} element={<Security />} />
                    <Route path={ROUTE_SLUGS.landing_pages['en-US']} element={<SignupLandingPage />} />
                </Route>

                {/* Spanish routes (/es prefix) */}
                <Route path="/es" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path={ROUTE_SLUGS.about['es-ES']} element={<About />} />
                    <Route path={ROUTE_SLUGS.features['es-ES']} element={<Features />} />
                    <Route path={ROUTE_SLUGS.pricing['es-ES']} element={<Pricing />} />
                    <Route path={ROUTE_SLUGS.cases['es-ES']} element={<Cases />} />
                    <Route path={ROUTE_SLUGS.contact['es-ES']} element={<Contact />} />
                    <Route path={ROUTE_SLUGS.platform['es-ES']} element={<Platform />} />
                    <Route path={ROUTE_SLUGS.workflow_demo['es-ES']} element={<WorkflowDemo />} />
                    <Route path={ROUTE_SLUGS.small_business['es-ES']} element={<SmallBusiness />} />
                    <Route path={ROUTE_SLUGS.api['es-ES']} element={<Api />} />
                    <Route path={ROUTE_SLUGS.hr['es-ES']} element={<HR />} />
                    <Route path={ROUTE_SLUGS.icp_brasil['es-ES']} element={<IcpBrasil />} />
                    <Route path={ROUTE_SLUGS.law['es-ES']} element={<Law />} />
                    <Route path={ROUTE_SLUGS.real_estate['es-ES']} element={<RealEstate />} />
                    <Route path={ROUTE_SLUGS.healthcare['es-ES']} element={<Healthcare />} />
                    <Route path={`${ROUTE_SLUGS.blog['es-ES']}/:slug`} element={<BlogPost />} />
                    <Route path={ROUTE_SLUGS.blog['es-ES']} element={<Blog />} />
                    <Route path={ROUTE_SLUGS.privacy['es-ES']} element={<Privacy />} />
                    <Route path={ROUTE_SLUGS.terms['es-ES']} element={<Terms />} />
                    <Route path={ROUTE_SLUGS.lgpd['es-ES']} element={<LGPD />} />
                    <Route path={ROUTE_SLUGS.security['es-ES']} element={<Security />} />
                    <Route path={ROUTE_SLUGS.landing_pages['es-ES']} element={<SignupLandingPage />} />
                </Route>

                {/* 404 fallback */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </>
    )
}

export default LocalizedRoutes
